import React from 'react';

import { ApolloClient, ApolloLink, HttpLink, InMemoryCache, InMemoryCacheConfig, from } from '@apollo/client';
import { loadDevMessages, loadErrorMessages } from '@apollo/client/dev';
import { CombinedGraphQLErrors, ServerError } from '@apollo/client/errors';
import { onError } from '@apollo/client/link/error';
import { ApolloProvider } from '@apollo/client/react';
import { relayStylePagination } from '@apollo/client/utilities';
import { centsScalar, dateScalar, retryLink } from '@betterment/graphql';
import { getCsrfToken } from '@betterment/js-runtime';
import { datadogLogs } from '@datadog/browser-logs';
import { withScalars } from 'apollo-link-scalars';
import { IntrospectionQuery, buildClientSchema } from 'graphql';
import { Observable } from 'rxjs';

import possibleTypesResult from '../graphql/schema';
import { data as schemaData } from '../graphql/schema.json';

type Headers = Record<string, string>;

export type GraphQLClientConfiguration = {
  url: string;
  headers?: Headers;
};

const httpLink = (url: string, customHeaders: Headers) => {
  return new HttpLink({
    credentials: 'include',
    headers: {
      'X-CSRF-Token': getCsrfToken() || '',
      'X-Requested-With': 'XMLHttpRequest',
      'Content-Type': 'application/json',
      ...customHeaders,
    },
    uri: url,
  });
};

const scalarsLink = withScalars({
  schema: buildClientSchema(schemaData as unknown as IntrospectionQuery),
  typesMap: {
    Cents: centsScalar,
    Date: dateScalar,
  },
});

const roundTripLink = new ApolloLink((operation, forward) => {
  const start = Date.now();
  operation.setContext({ start });

  return forward(operation);
});

const createErrorLink = (onUnauthenticatedError: () => Promise<void>) =>
  onError(({ error, operation }) => {
    if (CombinedGraphQLErrors.is(error)) {
      error.errors.forEach(gqlError => {
        datadogLogs.logger.error('WebGraphQLLatencyMetrix', {
          name: '[GraphQL Error]',
          message: gqlError.message,
          locations: gqlError.locations,
          path: gqlError.path,
        });
      });
    }

    if (ServerError.is(error) && error.statusCode === 401) {
      datadogLogs.logger.error('WebGraphQLNetworkError', {
        name: '[401 Error]',
        message: `operationName: ${operation.operationName} message: ${error.message}`,
      });

      return new Observable(observer => {
        onUnauthenticatedError()
          .then(() => {
            observer.complete();
          })
          .catch(err => {
            observer.error(err);
          });
      });
    }

    // onError callback type is ok with void but TS consistent-return is not
    // eslint-disable-next-line consistent-return
    return undefined;
  });

export const InMemoryCacheOptions: InMemoryCacheConfig = {
  possibleTypes: possibleTypesResult.possibleTypes,
  typePolicies: {
    Query: {
      fields: {
        activityConnection: relayStylePagination(),
      },
    },
    Solo401kConversionIntent: {
      fields: {
        planOwnerContributions: {
          merge(_, incoming: unknown[]) {
            return incoming;
          },
        },
        planSpouseContributions: {
          merge(_, incoming: unknown[]) {
            return incoming;
          },
        },
      },
    },
    ClientAccountTypeSummary: {
      fields: {
        primaryBeneficiaries: {
          keyArgs: ['id'],
          merge(existing, incoming: unknown[]) {
            return incoming;
          },
        },
        contingentBeneficiaries: {
          keyArgs: ['id'],
          merge(existing, incoming: unknown[]) {
            return incoming;
          },
        },
      },
    },
    BillingPlan: {
      fields: {
        billingRate: {
          merge(existing, incoming: unknown[]) {
            return incoming;
          },
        },
      },
    },
  },
};

const createApolloCache = () => new InMemoryCache(InMemoryCacheOptions);

const cache = createApolloCache();
const errorLink = createErrorLink(async () => {
  await cache.reset().finally(() => {
    window.location.href = '/advisors/sign_in';
  });
});

export const createGraphQLClient = (config: GraphQLClientConfiguration) => {
  const customHeaders = config.headers != null ? config.headers : {};

  const link = httpLink(config.url, customHeaders);

  return new ApolloClient({
    link: from([retryLink, errorLink, roundTripLink, scalarsLink, link]),
    cache: createApolloCache(),
  });
};

if (process.env.NODE_ENV !== 'production') {
  loadDevMessages();
  loadErrorMessages();
}

const client = createGraphQLClient({ url: '/api/graphql/web' });

/**
 * The root for setting up the GraphQL Provider to use [[useQuery]] and [[useMutation]] hooks
 *
 * The bare minimum setup using the default configuration:
 * ```typescript
 *
 * // within return statement of a component
 * <GraphQLProvider>
 *   // components depending on useQuery or useMutation
 * </GraphQLProvider>
 * ```
 */
export const GraphQLProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  return <ApolloProvider client={client}>{children}</ApolloProvider>;
};
